/**
 * defa.views.CustomerUpdateForm
 * A card for Updating a Customer.
 * @version 1.2.0
 */
import { defa } from 'defa-object';
import Form     from 'scripts/poppet/form';
import template from 'templates/customerUpdateForm.jst.html';

const channelNav  = Bb.Radio.channel( 'nav' ),
	channelUser = Bb.Radio.channel( 'user' );

const selectors = {
	inputType: '[data-input=customer-type]',
	
	inputsPrivate: '[data-input=customer-first-name], [data-input=customer-last-name]',
	inputsCompany: '[data-input^=customer-company]',
	
	inputFirstName    : '[data-input=customer-first-name]',
	inputLastName     : '[data-input=customer-last-name]',
	inputCompanyName  : '[data-input=customer-company-name]',
	inputCompanyNumber: '[data-input=customer-company-number]',
	
	inputDay  : '[data-input=customer-birth-date-day]',
	inputMonth: '[data-input=customer-birth-date-month]',
	inputYear : '[data-input=customer-birth-date-year]',
	
	inputContactEmail: '[data-input=customer-contact-mail]',
	
	inputAddress1: '[data-input=customer-address-1]',
	inputAddress2: '[data-input=customer-address-2]',
	inputPostCode: '[data-input=customer-postcode]',
	inputCity    : '[data-input=customer-city]',
	inputCountry : '[data-input=customer-country]',
	inputPhone   : '[data-input=customer-phone]',
	
	inputInvoiceSelections  : '[type=radio][data-input^=customer-invoice-]',
	inputInvoicePaper       : '[data-input=customer-invoice-paper]',
	inputInvoiceEmail       : '[data-input=customer-invoice-email]',
	inputInvoiceEmailAddress: '[data-input=customer-invoice-email-address]',
	
	buttonCustomers: '[data-click=customer-customers]',
	
};

const CustomerUpdateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template        : template,
	className       : 'card has-invoice-email has-type-private',
	noInitValidation: true,
	submitReaction  : 'disable',
	
	ui: {
		type: selectors.inputType,
		
		privates : selectors.inputsPrivate,
		companies: selectors.inputsCompany,
		
		firstName: selectors.inputFirstName,
		lastName : selectors.inputLastName,
		
		day  : selectors.inputDay,
		month: selectors.inputMonth,
		year : selectors.inputYear,
		
		companyName  : selectors.inputCompanyName,
		companyNumber: selectors.inputCompanyNumber,
		
		contactEmail: selectors.inputContactEmail,
		
		address1      : selectors.inputAddress1,
		address2      : selectors.inputAddress2,
		postcode      : selectors.inputPostCode,
		city          : selectors.inputCity,
		country       : selectors.inputCountry,
		invoice       : selectors.inputInvoiceSelections,
		invoicePaper  : selectors.inputInvoicePaper,
		invoiceEmail  : selectors.inputInvoiceEmail,
		invoiceAddress: selectors.inputInvoiceEmailAddress,
		
		customers: selectors.buttonCustomers,
	},
	
	events: {
		'input  @ui.type'     : 'onInputType',
		'change @ui.invoice'  : 'onInputInvoice',
		'click  @ui.customers': 'onClickCustomers',
	},
	
	templateContext: function () {
		return {
			icon        : this.model.get( 'type' ).toLowerCase(),
			isCustomerMe: this.isCustomerMe(),
		};
	},
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when the view renders
	 * @void
	 */
	onRender: function () {
		this.createCountryHTML();
		this.createBirthHTML();
		this.checkInvoiceSettings();
		
		if ( 'PRIVATE' === this.model.get( 'type' ) ) {
			this.setAsPrivate();
		} else {
			this.setAsCompany();
		}
	},
	
	/**
	 * Triggers when the type field changes.
	 * @void
	 */
	onInputType: function () {
		let input = this.ui.type.val().trim();
		
		if ( 'PRIVATE' === input ) {
			this.setAsPrivate();
		}
		
		if ( 'COMPANY' === input ) {
			this.setAsCompany();
		}
	},
	
	/**
	 * Triggers when the invoice field changes.
	 * @void
	 */
	onInputInvoice: function () {
		this.checkInvoiceSettings();
	},
	
	/**
	 * Triggers when the "customers" button is clicked
	 * @void
	 */
	onClickCustomers: function () {
		channelNav.trigger( 'customer:list', [ channelUser.request( 'id' ), ] );
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - html functions
	
	/**
	 * Fills the three birth date selects.
	 * @void
	 */
	createBirthHTML: function () {
		
		let date  = this.model.get( 'birthDate' ),
			day   = this.ui.day,
			month = this.ui.month,
			year  = this.ui.year,
			arr   = date.split( '-' );
		
		// first create the days:
		
		let firstDay  = 1,
			lastDay   = 31,
			daysHtml5 = '';
		
		for ( firstDay; firstDay < ( lastDay + 1 ); firstDay++ ) {
			// current day is arr[2]
			let value    = ( '0' + firstDay ).slice( -2 ),
				selected = ( arr[ 2 ] === value ) ? ' selected' : '';
			
			daysHtml5 += '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', value )
				.replace( '%s2', selected )
				.replace( '%s3', firstDay );
		}
		
		day.append( daysHtml5 );
		
		
		// then create months
		let firstMonth  = 1,
			lastMonth   = 12,
			monthsHtml5 = '';
		
		for ( firstMonth; firstMonth < ( lastMonth + 1 ); firstMonth++ ) {
			// current month is arr[1]
			let value    = ( '0' + firstMonth ).slice( -2 ),
				selected = ( arr[ 1 ] === value ) ? ' selected' : '';
			
			monthsHtml5 += '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', value )
				.replace( '%s2', selected )
				.replace( '%s3', firstMonth );
		}
		
		month.append( monthsHtml5 );
		
		
		// then create year
		let firstYear  = new Date().getFullYear(),
			lastYear   = 1900,
			yearsHtml5 = '';
		
		for ( firstYear; ( firstYear + 1 ) > lastYear; firstYear-- ) {
			// current year is arr[0]
			let value    = String( firstYear ),
				selected = ( arr[ 0 ] === value ) ? ' selected' : '';
			
			yearsHtml5 += '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', value )
				.replace( '%s2', selected )
				.replace( '%s3', firstYear );
		}
		
		year.append( yearsHtml5 );
		
	},
	
	/**
	 * Creates a list of html option elements to fill the country select with.
	 * @void
	 */
	createCountryHTML: function () {
		
		let self      = this,
			html5     = '',
			select    = self.ui.country,
			countries = defa.vars.countries;
		
		_.each( countries, function ( country ) {
			let selected = ( self.model.get( 'countryCode' ) === country.alpha2 )
						   ? ' selected'
						   : '';
			
			let str = '<option value="%s1"%s2>%s3</option>'
				.replace( '%s1', country.alpha2 )
				.replace( '%s2', selected )
				.replace( '%s3', country.country ); // name
			
			html5 += str;
		} );
		
		select.append( html5 );
	},
	
	// - helper functions
	
	/**
	 * Checks if the current user is associated with the logged in user.
	 * @void
	 */
	isCustomerMe: function () {
		let user     = channelUser.request( 'id' ),
			customer = this.model.get( 'id' ),
			maps     = _.where( defa.vars.mapUsersCustomers, { user: user, customer: customer } );
		
		return ( 1 <= maps.length );
	},
	
	// - form setting functions
	
	/**
	 * Changes the form to match input fields for a private Customer.
	 * @void
	 */
	setAsPrivate: function () {
		this.$el
			.toggleClass( 'has-type-private', true )
			.toggleClass( 'has-type-company', false );
		
		this.ui.privates.prop( 'required', true );
		this.ui.companies.removeAttr( 'required' );
	},
	
	/**
	 * Changes the form to match input fields for a company Customer.
	 * @void
	 */
	setAsCompany: function () {
		this.$el
			.toggleClass( 'has-type-private', false )
			.toggleClass( 'has-type-company', true );
		
		this.ui.privates.removeAttr( 'required' );
		this.ui.companies.prop( 'required', true );
	},
	
	/**
	 * Shows or hides the extra invoice fields based on customer selection.
	 * @void
	 */
	checkInvoiceSettings: function () {
		let invoiceTypes = _.pluck( this.ui.invoice.filter( ':checked' ), 'value' );
		
		if ( _.contains( invoiceTypes, 'EMAIL' ) ) {
			this.setEmailInvoice();
		} else {
			this.unsetEmailInvoice();
		}
		
	},
	
	/**
	 * Sets email as the preferred invoice
	 * @void
	 */
	setEmailInvoice: function () {
		this.$el
			.toggleClass( 'has-invoice-paper', false )
			.toggleClass( 'has-invoice-email', true );
		
		this.ui.invoiceAddress.prop( 'required', true );
	},
	
	/**
	 * Unsets email as the preffered invoice type.
	 * @void
	 */
	unsetEmailInvoice: function () {
		this.$el
			.toggleClass( 'has-invoice-paper', true )
			.toggleClass( 'has-invoice-email', false );
		
		this.ui.invoiceAddress.removeAttr( 'required' );
	},
	
	// - send functions
	
	/**
	 * Returns the customer birth date as ISO 8601 if it exists.
	 * @return {string}  An ISO 8601 date string, or an empty string.
	 */
	getBirthDate: function () {
		// check date
		let year  = this.ui.year.val().trim(),
			month = this.ui.month.val().trim(),
			day   = this.ui.day.val().trim();
		
		return ( year && month && day )
			   ? '%s1-%s2-%s3'
				   .replace( '%s1', year )
				   .replace( '%s2', month )
				   .replace( '%s3', day )
			   : '';
	},
	
	/**
	 * Submits the form.
	 * @void
	 */
	submit: function () {
		
		let form = {
			
			firstName: this.ui.firstName.val().trim(),
			lastName : this.ui.lastName.val().trim(),
			
			birthDate: this.getBirthDate(),
			
			companyName  : this.ui.companyName.val().trim(),
			companyNumber: this.ui.companyNumber.val().trim(),
			
			contactEmail: this.ui.contactEmail.val().trim(),
			
			address1   : this.ui.address1.val().trim(),
			address2   : this.ui.address2.val().trim(),
			postcode   : this.ui.postcode.val().trim(),
			city       : this.ui.city.val().trim(),
			countryCode: this.ui.country.val().trim(),
			
			invoiceTypes: _.pluck( this.ui.invoice.filter( ':checked' ), 'value' ),
			invoiceEmail: this.ui.invoiceAddress.val().trim(),
		};
		
		if ( 'PRIVATE' === this.model.type ) {
			delete form.companyName;
			delete form.companyNumber;
		}
		
		if ( 'COMPANY' === this.model.type ) {
			delete form.firstName;
			delete form.lastName;
		}
		
		this.model.save( form );
	},
	
} );

export default CustomerUpdateForm;